// src/components/patients/PatientList.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogContentText,
  styled,
  Fade,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  Chip,
  Pagination,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  SwipeableDrawer,
  Fab,
  Stack,
  Badge,
  Container,
  AppBar,
  Toolbar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Cake as CakeIcon,
  Wc as WcIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import agthealthService from '../../../services/agthealthService';
import PatientModal from './PatientModal';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';

// Definir color verde personalizado
const appleGreen = '#4CAF50';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  paddingBottom: theme.spacing(16), // Más espacio inferior (~128px)
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: 0,
    paddingBottom: theme.spacing(14), // Más espacio inferior en móvil (~112px)
  }
}));

// Componentes estilizados para el estilo Apple
const AppleDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(3px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '14px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: theme.spacing(1),
    maxWidth: '450px',
    width: '100%',
    margin: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0),
      borderRadius: 0,
      width: '100%',
      height: '100%',
      maxWidth: '100%',
    }
  }
}));

const AppleDialogTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2, 2, 1),
  '& h2': {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#1d1d1f'
  }
}));

const AppleDialogContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3, 2),
  textAlign: 'center',
  '& p': {
    color: '#86868b',
    fontSize: '0.95rem',
    lineHeight: 1.5
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2, 2),
  }
}));

const AppleDialogActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1, 2, 2),
  gap: theme.spacing(1)
}));

const AppleButton = styled(Button)(({ theme, variant, size }) => ({
  borderRadius: '12px',
  padding: size === 'small' ? theme.spacing(0.8, 1.5) : theme.spacing(1.2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: size === 'small' ? '0.85rem' : '0.95rem',
  backgroundColor: variant === 'contained' ? appleGreen : 'transparent',
  color: variant === 'contained' ? 'white' : appleGreen,
  border: variant === 'outlined' ? `1px solid ${appleGreen}` : 'none',
  '&:hover': {
    backgroundColor: variant === 'contained' 
      ? theme.palette.success.dark 
      : 'rgba(76, 175, 80, 0.1)',
  },
}));

const StyledIconButton = styled(IconButton)({
  color: appleGreen,
});

// Tabla personalizada sin sombra, solo con borde
const BorderedTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(224, 224, 224, 1)',
  boxShadow: 'none',
  overflow: 'hidden',
}));

// Componente de búsqueda estilizado
const SearchContainer = styled(Box)(({ theme, isMobile }) => ({
  marginBottom: theme.spacing(isMobile ? 2 : 3),
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: theme.spacing(isMobile ? 1.5 : 2),
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  '& .MuiChip-deleteIcon': {
    color: appleGreen,
    '&:hover': {
      color: theme.palette.success.dark,
    },
  },
}));

// Tarjeta de paciente para vista móvil
const PatientCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
  }
}));

// Botón flotante para móvil
const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 80,
  right: 20,
  backgroundColor: appleGreen,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  zIndex: 1000,
}));

const PatientList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  // Estados para la lista de pacientes
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, patientId: null });
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState({
    firstName: 'Dr.',
    lastName: 'Usuario',
    specialty: 'Profesional'
  });
  
  // Estados para la búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    gender: '',
    ageMin: '',
    ageMax: '',
    chronicDisease: '',
  });
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: isMobile ? 10 : 20
  });

  // Estados para el modal
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  // Estados para sugerencias de enfermedades crónicas
  const [commonDiseases, setCommonDiseases] = useState([]);
  
  // Estado para menú de acciones en móvil
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  // Ajustar límite de paginación según el tamaño de pantalla
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      limit: isMobile ? 10 : 20
    }));
  }, [isMobile]);

  // Función para cargar los pacientes con búsqueda y filtros
  const fetchPatients = async (page = 1) => {
    try {
      setLoading(true);
      
      // Crear objeto de filtros para la búsqueda
      const searchFilters = {};
      
      if (searchTerm) {
        searchFilters.search = searchTerm;
      }
      
      // Añadir filtros activos
      activeFilters.forEach(filter => {
        searchFilters[filter.type] = filter.value;
      });
      
      console.log('Buscando pacientes con filtros:', searchFilters);
      
      const response = await agthealthService.searchPatients(searchFilters, page, pagination.limit);
      
      if (response.success) {
        setPatients(response.data || []);
        setPagination({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          limit: response.pagination.limit
        });
        setError(null);
      } else {
        setError('Error al cargar pacientes');
      }
    } catch (err) {
      console.error('Error al cargar pacientes:', err);
      setError('No se pudieron cargar los pacientes. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar enfermedades crónicas comunes para sugerencias
  const fetchCommonDiseases = async () => {
    try {
      const response = await agthealthService.getCommonChronicDiseases();
      if (response.success) {
        setCommonDiseases(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar enfermedades comunes:', error);
    }
  };

  useEffect(() => {
    fetchPatients(1);
    fetchCommonDiseases();
    
    // Cargar información del usuario
    const fetchUserProfile = async () => {
      try {
        const response = await agthealthService.getUserProfile();
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error al cargar perfil de usuario:', error);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Efecto para cuando cambia la búsqueda o los filtros
  useEffect(() => {
    // Aplicar un debounce para evitar muchas solicitudes seguidas
    const handler = setTimeout(() => {
      fetchPatients(1); // Reiniciar a la primera página
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, activeFilters]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleAddPatient = () => {
    setPatientModal({ open: true, patientId: null });
  };

  const handleEditPatient = (patientId) => {
    setPatientModal({ open: true, patientId });
  };

  const handleViewPatient = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  const handleDeleteClick = (patientId) => {
    setDeleteDialog({ open: true, patientId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await agthealthService.deletePatient(deleteDialog.patientId);
      // Recargar la lista después de eliminar
      fetchPatients(pagination.currentPage);
      setDeleteDialog({ open: false, patientId: null });
      handleCloseActionMenu();
    } catch (err) {
      console.error('Error al eliminar paciente:', err);
      setError('No se pudo eliminar el paciente. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, patientId: null });
  };
  
  const handleClosePatientModal = () => {
    setPatientModal({ ...patientModal, open: false });
  };
  
  const handlePatientSaved = () => {
    // Recargar la lista de pacientes después de guardar
    fetchPatients(pagination.currentPage);
  };

  // Manejadores para la búsqueda y filtros
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value
    });
  };

  const handleApplyFilter = (filterType) => {
    const filterValue = filters[filterType];
    if (!filterValue) return;

    // Verificar si el filtro ya existe
    const filterExists = activeFilters.some(
      filter => filter.type === filterType && filter.value === filterValue
    );

    if (!filterExists) {
      let filterLabel = '';
      switch (filterType) {
        case 'gender':
          filterLabel = `Género: ${filterValue === 'M' ? 'Masculino' : 'Femenino'}`;
          break;
        case 'ageMin':
          filterLabel = `Edad mínima: ${filterValue}`;
          break;
        case 'ageMax':
          filterLabel = `Edad máxima: ${filterValue}`;
          break;
        case 'chronicDisease':
          filterLabel = `Enfermedad: ${filterValue}`;
          break;
        default:
          filterLabel = `${filterType}: ${filterValue}`;
      }

      setActiveFilters([...activeFilters, { 
        type: filterType, 
        value: filterValue,
        label: filterLabel
      }]);

      // Limpiar el campo de filtro después de aplicarlo
      setFilters({
        ...filters,
        [filterType]: ''
      });
    }
  };

  const handleRemoveFilter = (indexToRemove) => {
    setActiveFilters(activeFilters.filter((_, index) => index !== indexToRemove));
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilters({
      gender: '',
      ageMin: '',
      ageMax: '',
      chronicDisease: '',
    });
  };

  const handlePageChange = (event, newPage) => {
    fetchPatients(newPage);
  };
  
  // Manejo del menú de acciones para móvil
  const handleOpenActionMenu = (patientId) => {
    setSelectedPatientId(patientId);
    setActionMenuOpen(true);
  };
  
  const handleCloseActionMenu = () => {
    setActionMenuOpen(false);
    setSelectedPatientId(null);
  };
  
  // Obtener paciente seleccionado
  const getSelectedPatient = () => {
    return patients.find(patient => patient.patientId === selectedPatientId) || null;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onNewPatient={handleAddPatient}
        onLogout={handleLogout}
        activeSection="patients"
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title="Mis Pacientes"
          user={user} 
          isMobile={isMobile} 
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        <Box>
          {/* Sección de búsqueda y filtros */}
          <SearchContainer isMobile={isMobile}>
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              {/* Campo de búsqueda con título por encima */}
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500, 
                  color: '#424242',
                  fontSize: '0.875rem'
                }}
              >
                Buscar paciente
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Nombre o identificación"
                value={searchTerm}
                onChange={handleSearchChange}
                size={isSmall ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: appleGreen, fontSize: isSmall ? 18 : 24 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={handleClearSearch} 
                        size="small"
                        sx={{ 
                          color: '#999',
                          '&:hover': { color: '#666' }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                      boxShadow: 'none',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.3)',
                    }
                  }
                }}
                sx={{ mb: isMobile ? 1.5 : 2 }}
              />
              
              {/* Botón de filtros */}
              <Button
                startIcon={<FilterListIcon fontSize={isSmall ? "small" : "medium"} />}
                endIcon={showFilters ? <KeyboardArrowUpIcon fontSize={isSmall ? "small" : "medium"} /> : <KeyboardArrowDownIcon fontSize={isSmall ? "small" : "medium"} />}
                onClick={handleToggleFilters}
                size={isSmall ? "small" : "medium"}
                sx={{
                  color: appleGreen,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  textTransform: 'none',
                  py: isSmall ? 0.75 : 1,
                  px: isSmall ? 1.5 : 2,
                  fontWeight: 500,
                  fontSize: isSmall ? '0.8rem' : 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.15)',
                  }
                }}
              >
                {isSmall ? "Filtros" : "Filtros avanzados"}
              </Button>
            </Box>

            {/* Filtros expandibles con estilo Apple */}
            <Collapse in={showFilters}>
              <Box 
                mt={2} 
                p={isSmall ? 1.5 : 3} 
                sx={{
                  backgroundColor: '#f5f5f7',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.05)'
                }}
              >
                <Grid container spacing={isSmall ? 2 : 3}>
                  {/* Filtro de género */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Género
                    </Typography>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      size={isSmall ? "small" : "medium"}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: appleGreen,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    >
                      <Select
                        name="gender"
                        value={filters.gender}
                        onChange={handleFilterChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Género' }}
                      >
                        <MenuItem value=""><em>Seleccionar</em></MenuItem>
                        <MenuItem value="M">Masculino</MenuItem>
                        <MenuItem value="F">Femenino</MenuItem>
                      </Select>
                    </FormControl>
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('gender')}
                        disabled={!filters.gender}
                        size={isSmall ? "small" : "medium"}
                        sx={{ 
                          color: appleGreen,
                          textTransform: 'none',
                          fontWeight: 500,
                          '&.Mui-disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                          }
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Edad mínima */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Edad mínima
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="ageMin"
                      type="number"
                      value={filters.ageMin}
                      onChange={handleFilterChange}
                      size={isSmall ? "small" : "medium"}
                      InputProps={{ 
                        inputProps: { min: 0, max: 120 },
                        sx: {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: appleGreen,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    />
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('ageMin')}
                        disabled={!filters.ageMin}
                        size={isSmall ? "small" : "medium"}
                        sx={{ 
                          color: appleGreen,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Edad máxima */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Edad máxima
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="ageMax"
                      type="number"
                      value={filters.ageMax}
                      onChange={handleFilterChange}
                      size={isSmall ? "small" : "medium"}
                      InputProps={{ 
                        inputProps: { min: 0, max: 120 },
                        sx: {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: appleGreen,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    />
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('ageMax')}
                        disabled={!filters.ageMax}
                        size={isSmall ? "small" : "medium"}
                        sx={{ 
                          color: appleGreen,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Enfermedad crónica */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Enfermedad crónica
                    </Typography>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      size={isSmall ? "small" : "medium"}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: appleGreen,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    >
                      <Select
                        name="chronicDisease"
                        value={filters.chronicDisease}
                        onChange={handleFilterChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Enfermedad crónica' }}
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: 300 }
                          }
                        }}
                      >
                        <MenuItem value=""><em>Seleccionar</em></MenuItem>
                        {commonDiseases.map((disease, index) => (
                          <MenuItem key={index} value={disease.name}>
                            {disease.name}
                          </MenuItem>
                        ))}
                        <MenuItem value="other">
                          <em>Otra enfermedad...</em>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {filters.chronicDisease === 'other' && (
                      <TextField
                        fullWidth
                        placeholder="Escriba la enfermedad"
                        variant="outlined"
                        name="customDisease"
                        value={filters.customDisease || ''}
                        onChange={(e) => setFilters({...filters, customDisease: e.target.value})}
                        size={isSmall ? "small" : "medium"}
                        sx={{ 
                          mt: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(76, 175, 80, 0.5)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: appleGreen,
                              borderWidth: '1px',
                            },
                          }
                        }}
                      />
                    )}
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('chronicDisease')}
                        disabled={!filters.chronicDisease || (filters.chronicDisease === 'other' && !filters.customDisease)}
                        size={isSmall ? "small" : "medium"}
                        sx={{ 
                          color: appleGreen,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {/* Chips de filtros activos con estilo Apple */}
            {activeFilters.length > 0 && (
              <Box 
                mt={2} 
                display="flex" 
                flexWrap="wrap" 
                alignItems="center"
                sx={{ py: 1 }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666', 
                    mr: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {isSmall ? "Filtros:" : "Filtros activos:"}
                </Typography>
                {activeFilters.map((filter, index) => (
                  <FilterChip
                    key={index}
                    label={filter.label}
                    onDelete={() => handleRemoveFilter(index)}
                    size="small"
                    sx={{
                      mr: 1,
                      mb: 1,
                      borderRadius: '20px',
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: appleGreen,
                      fontWeight: 500,
                      border: '1px solid rgba(76, 175, 80, 0.2)',
                      '& .MuiChip-deleteIcon': {
                        color: appleGreen,
                        '&:hover': {
                          color: '#2e7d32'
                        }
                      }
                    }}
                  />
                ))}
                <Button 
                  size="small" 
                  onClick={handleClearAllFilters} 
                  sx={{ 
                    ml: 1,
                    mb: 1, 
                    color: '#666',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: appleGreen,
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  {isSmall ? "Limpiar" : "Limpiar todos"}
                </Button>
              </Box>
            )}
          </SearchContainer>

          {/* Botón para agregar paciente (solo en desktop) */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <AppleButton 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleAddPatient}
              >
                Nuevo Paciente
              </AppleButton>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress style={{ color: appleGreen }} />
            </Box>
          ) : patients.length === 0 ? (
            <Alert severity="info" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: appleGreen }}>
              {searchTerm || activeFilters.length > 0 
                ? "No se encontraron pacientes con los criterios de búsqueda actuales."
                : "No tienes pacientes registrados. Comienza añadiendo tu primer paciente."
              }
            </Alert>
          ) : (
            <>
              {/* Vista de escritorio: Tabla */}
              {!isMobile && (
                <BorderedTableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Identificación</TableCell>
                        <TableCell>Edad</TableCell>
                        <TableCell>Género</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patients.map((patient) => (
                        <TableRow key={patient.patientId} sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.04)' } }}>
                          <TableCell>{`${patient.firstName} ${patient.lastName}`}</TableCell>
                          <TableCell>{patient.identificationNumber}</TableCell>
                          <TableCell>{patient.age || 'N/A'}</TableCell>
                          <TableCell>{patient.gender === 'M' ? 'Masculino' : patient.gender === 'F' ? 'Femenino' : patient.gender || 'N/A'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver detalles">
                              <StyledIconButton onClick={() => handleViewPatient(patient.patientId)}>
                                <VisibilityIcon />
                              </StyledIconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <StyledIconButton onClick={() => handleEditPatient(patient.patientId)}>
                                <EditIcon />
                              </StyledIconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <StyledIconButton onClick={() => handleDeleteClick(patient.patientId)}>
                                <DeleteIcon />
                              </StyledIconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </BorderedTableContainer>
              )}
              
              {/* Vista móvil: Tarjetas */}
              {isMobile && (
                <Box sx={{ mb: 2 }}>
                  {patients.map((patient) => (
                    <PatientCard key={patient.patientId}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {`${patient.firstName} ${patient.lastName}`}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenActionMenu(patient.patientId)}
                            sx={{ padding: '4px', marginRight: -1 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BadgeIcon sx={{ fontSize: 14, mr: 0.5, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                ID: {patient.identificationNumber || 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WcIcon sx={{ fontSize: 14, mr: 0.5, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {patient.gender === 'M' ? 'Masculino' : patient.gender === 'F' ? 'Femenino' : patient.gender || 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CakeIcon sx={{ fontSize: 14, mr: 0.5, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {patient.age ? `${patient.age} años` : 'Edad: N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                      
                      <CardActions sx={{ pt: 0, pb: 1, px: 2 }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleViewPatient(patient.patientId)}
                          sx={{ color: appleGreen }}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleEditPatient(patient.patientId)}
                          sx={{ color: appleGreen }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteClick(patient.patientId)}
                          sx={{ color: '#f44336' }}
                        >
                          Eliminar
                        </Button>
                      </CardActions>
                    </PatientCard>
                  ))}
                </Box>
              )}
              
              {/* Paginación adaptada */}
              {pagination.totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: isMobile ? 2 : 3,
                  '& .MuiPagination-ul': {
                    flexWrap: isSmall ? 'nowrap' : 'wrap'
                  }
                }}>
                  <Pagination 
                    count={pagination.totalPages} 
                    page={pagination.currentPage} 
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size={isSmall ? "small" : "medium"}
                    siblingCount={isSmall ? 0 : 1}
                    boundaryCount={isSmall ? 1 : 2}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: appleGreen,
                      },
                      '& .Mui-selected': {
                        backgroundColor: `${appleGreen}!important`,
                        color: 'white',
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}

          {/* Diálogo de confirmación para eliminar al estilo Apple con blur en el fondo */}
          <AppleDialog
            open={deleteDialog.open}
            onClose={handleDeleteCancel}
            TransitionComponent={Fade}
            transitionDuration={300}
            keepMounted
            fullScreen={isSmall}
          >
            {/* Barra superior especial para móvil */}
            {isSmall && (
              <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={handleDeleteCancel}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                    Confirmar eliminación
                  </Typography>
                </Toolbar>
              </AppBar>
            )}
            
            {!isSmall && (
              <AppleDialogTitle>
                <Typography variant="h2">Confirmar eliminación</Typography>
              </AppleDialogTitle>
            )}
            
            <AppleDialogContent>
              <DialogContentText>
                ¿Estás seguro de que deseas eliminar este paciente? Esta acción no se puede deshacer.
              </DialogContentText>
            </AppleDialogContent>
            
            <AppleDialogActions>
              <AppleButton 
                variant="contained" 
                onClick={handleDeleteConfirm} 
                fullWidth
                size={isSmall ? "small" : "medium"}
              >
                Eliminar
              </AppleButton>
              <AppleButton 
                variant="outlined" 
                onClick={handleDeleteCancel} 
                fullWidth
                size={isSmall ? "small" : "medium"}
              >
                Cancelar
              </AppleButton>
            </AppleDialogActions>
          </AppleDialog>
          
          {/* Modal de paciente (nuevo/editar) */}
          <PatientModal
            open={patientModal.open}
            onClose={handleClosePatientModal}
            patientId={patientModal.patientId}
            onSaved={handlePatientSaved}
            fullScreen={isSmall}
          />
          
          {/* Botón flotante para agregar paciente (solo en móvil) */}
          {isMobile && (
            <FloatingActionButton 
              color="primary" 
              aria-label="nuevo paciente" 
              onClick={handleAddPatient}
            >
              <AddIcon />
            </FloatingActionButton>
          )}
          
          {/* Menú de acciones para móvil (bottom drawer) */}
          {isMobile && (
            <SwipeableDrawer
              anchor="bottom"
              open={actionMenuOpen}
              onClose={handleCloseActionMenu}
              onOpen={() => {}}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                  maxWidth: '100%',
                  width: '100%',
                },
              }}
            >
              <Box sx={{ py: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 600,
                    py: 1,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    position: 'relative'
                  }}
                >
                  {getSelectedPatient() && `${getSelectedPatient().firstName} ${getSelectedPatient().lastName}`}
                  <IconButton
                    onClick={handleCloseActionMenu}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Typography>
                
                <List sx={{ pt: 0 }}>
                  {getSelectedPatient() && (
                    <>
                      <ListItem 
                        button 
                        onClick={() => {
                          handleViewPatient(selectedPatientId);
                          handleCloseActionMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <VisibilityIcon fontSize="small" sx={{ color: appleGreen }} />
                        </ListItemIcon>
                        <ListItemText primary="Ver detalles del paciente" />
                      </ListItem>
                      
                      <ListItem 
                        button 
                        onClick={() => {
                          handleEditPatient(selectedPatientId);
                          handleCloseActionMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <EditIcon fontSize="small" sx={{ color: appleGreen }} />
                        </ListItemIcon>
                        <ListItemText primary="Editar paciente" />
                      </ListItem>
                      
                      <ListItem 
                        button 
                        onClick={() => {
                          handleDeleteClick(selectedPatientId);
                          handleCloseActionMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Eliminar paciente" 
                          primaryTypographyProps={{ sx: { color: '#f44336' } }}
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Box>
            </SwipeableDrawer>
          )}
        </Box>
      </Main>
    </Box>
  );
};

export default PatientList;